import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import RegisterFailureApi from "../../services/FailureApi";
import OperationApi from "../../services/OperationApi";
import FmeaApi from "../../services/FmeaApi";
import FamilyFmeaApi from "../../services/FamilyFmeaApi";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import FileUploader from "../../components/FileUploader/FileUploader";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface Option {
  id: string;
  name: string;
}

const CreateFailureForm: React.FC = () => {
  const { plantId } = useAuth();
  const { fmeaId } = useParams<{ fmeaId: string }>();
  const [searchParams] = useSearchParams();
  const fmeaSource = searchParams.get("type");
  
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { t: translate } = useTranslation("form");
  
  const [operacoes, setOperacoes] = useState<Option[]>([]);
  const [operacao, setOperacao] = useState<string>("");
  const [descricao, setDescricao] = useState("");
  const [efeito, setEfeito] = useState("");
  const [causas, setCausas] = useState<Option[]>([]);
  const [causa, setCausa] = useState<string>("");
  const [causeDescription, setCauseDescription] = useState("");
  const [severidade, setSeveridade] = useState("");
  const [ocorrencia, setOcorrencia] = useState("");
  const [detectabilidade, setDetectabilidade] = useState("");
  const [rpn, setRpn] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sublineId, setSublineId] = useState<string | undefined>(undefined);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    if (!fmeaId) return;
    const loadFmea: any = async () => {
      try {
        let resp;
        if (fmeaSource === "family") {
          resp = await FamilyFmeaApi.GetById(fmeaId);
        } else {
          resp = await FmeaApi.GetById(fmeaId);
        }
        setSublineId(resp.sublineId);
      } catch (err: any) {
        displayToast("error", translate("error"), err?.message ?? "", false, 3000);
      }
    };
    loadFmea();
  }, [fmeaId, fmeaSource, displayToast, translate]);

  useEffect(() => {
    const fetchCauses = async (): Promise<void> => {
      try {
        const causasResponse = await RegisterFailureApi.GetAllCauses();
        setCausas(causasResponse.data || []);
      } catch {
        displayToast("error", translate("errorFetchingData"), "", false, 3000);
      }
    };
    fetchCauses();
  }, [displayToast, translate]);

  useEffect(() => {
    if (!sublineId) return;
    const fetchOperations = async (): Promise<void> => {
      try {
        const response = await OperationApi.GetByFilter(
          [plantId], 1, 1000, "name", "asc",
          undefined, undefined, undefined, undefined, [], [], [], sublineId ? [sublineId] : [],
        );
        const ops = Array.isArray(response.data.operations) ? response.data.operations : [];
        setOperacoes(ops);
      } catch {
        console.error("Erro ao buscar operações filtradas");
      }
    };
    fetchOperations();
  }, [sublineId, plantId]);

  useEffect(() => {
    const s = parseInt(severidade || "0");
    const o = parseInt(ocorrencia || "0");
    const d = parseInt(detectabilidade || "0");
    if (!isNaN(s) && !isNaN(o) && !isNaN(d) && s > 0 && o > 0 && d > 0) {
      setRpn((s * o * d).toString());
    } else {
      setRpn("");
    }
  }, [severidade, ocorrencia, detectabilidade]);

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    const causeObj = causas.find((c) => c.id === causa);

    if (!operacao || !descricao || !efeito || !causeObj?.id || !causeDescription || !severidade || !ocorrencia || !detectabilidade) {
      displayToast("warn", translate("warning"), translate("fillInAllRequiredFields"));
      return;
    }

    const formData = new FormData();
    formData.append("Name", descricao);
    formData.append("Description", descricao);
    formData.append("Effect", efeito);
    formData.append("Severity", severidade);
    formData.append("Occurrence", ocorrencia);
    formData.append("Detectability", detectabilidade);
    formData.append("OperationId", operacao);
    formData.append("plantId", plantId);
    formData.append("CauseId", causeObj.id);
    formData.append("CauseDescription", causeDescription);
    if (fmeaId) formData.append("FmeaId", fmeaId);
    files.forEach((file) => formData.append("Files", file));
    formData.append("FmeaCategory", fmeaSource === "family" ? "1" : "0");

    try {
      await RegisterFailureApi.Create(formData);
      displayToast("success", translate("Success"), translate("failureSuceffullyCreated"), false, 3000);
      navigate(`/fmeas/${fmeaId}/failures?type=${fmeaSource}`);
    } catch {
      displayToast("error", translate("error"), translate("failureNotCreated"), false, 3000);
    }
  };

  const selectedCauseObj = causas.find((c) => c.id === causa) || null;

  return (
    <>
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate(`/fmeas/${fmeaId}/failures?type=${fmeaSource}`)}
      />
      <FormCard
        title={translate("registerPotentialFailure")}
        icon="warning"
        submitButtonLabel={translate("register")}
        cancelButtonLabel={translate("Cancel")}
        onSubmit={handleSave}
        onCancel={handleCancel}
        numColumns={12}
      >
        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="select"
            label={translate("operation")}
            value={operacao}
            options={operacoes.map((o) => ({ label: o.name, value: o.id }))}
            optionLabel="label"
            optionValue="value"
            required
            placeholder={`${translate("selectOperation")} *`}
            onChange={(val: any) => setOperacao(val)}
          />
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <Input
            type="textArea"
            label={translate("Description")}
            value={descricao}
            required
            rows={3}
            placeholder={`${translate("Description")} *`}
            onChange={(val: any) => setDescricao(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="select"
            label={translate("cause")}
            value={causa}
            options={causas.map((c) => ({ label: c.name, value: c.id }))}
            optionLabel="label"
            optionValue="value"
            required
            placeholder={`${translate("selectCause")} *`}
            onChange={(val: any) => setCausa(val)}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            type="textArea"
            label={translate("effect")}
            value={efeito}
            required
            rows={1}
            placeholder={`${translate("effect")} *`}
            onChange={(val: any) => setEfeito(val)}
          />
        </div>

        {selectedCauseObj && (
          <div style={{ gridColumn: "span 12" }}>
            <Input
              type="textArea"
              label={translate("causeDescription")}
              value={causeDescription}
              required
              rows={2}
              placeholder={`${translate("causeDescription")} *`}
              onChange={(val: any) => setCauseDescription(val)}
            />
          </div>
        )}

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="text"
            label={translate("severity")}
            value={severidade}
            required
            placeholder="1–10"
            onChange={(val: any) => setSeveridade(val)}
          />
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="text"
            label={translate("occurrence")}
            value={ocorrencia}
            required
            placeholder="1–10"
            onChange={(val: any) => setOcorrencia(val)}
          />
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="text"
            label={translate("detectability")}
            value={detectabilidade}
            required
            placeholder="1–10"
            onChange={(val: any) => setDetectabilidade(val)}
          />
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="text"
            label="RPN"
            value={rpn}
            disabled
            onChange={() => {}}
          />
          <small style={{ color: "var(--color-neutral-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {translate("rpn")}
          </small>
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <FileUploader
            files={files}
            onFilesChange={setFiles}
            labels={{
              title: translate("attachFiles") || "Anexos",
              choose: translate("choose") || "Escolher",
              remove: translate("remove") || "Remover",
            }}
          />
        </div>
      </FormCard>
    </>
  );
};

export default CreateFailureForm;
